/**
 * useCycleComplete
 *
 * Derives cycle completion data from real hooks.
 * Provides results, cryo storage info, and "what's next" options.
 */

import { useMemo } from "react";
import {
  useCycles,
  useCyclePhases,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import { useFrozenSpecimensForCycle } from "@src/packages/care/cryovault/useSpecimens";
import {
  DEV_FORCE_COMPLETE_CYCLE_OVERRIDE,
  MOCK_COMPLETED_CRYO_STORAGE,
  MOCK_COMPLETED_CYCLE_RESULT,
  MOCK_COMPLETED_WHATS_NEXT,
} from "@src/packages/cycles/dev/devCompleteCycleOverride";
import type {
  CycleResult,
  CryoStorageInfo,
  WhatsNextOption,
} from "../components/CycleCompleteView";
import type { HomepageGoal } from "../../homepage.types";

export type UseCycleCompleteResult = {
  isComplete: boolean;
  isLoading: boolean;
  result: CycleResult | undefined;
  cryoStorage: CryoStorageInfo | undefined;
  whatsNextOptions: WhatsNextOption[];
};

export function useCycleComplete(
  goal: HomepageGoal | null,
  intendedCycleType?: CycleType | null,
): UseCycleCompleteResult {
  const { data: cycles, isLoading: cyclesLoading } = useCycles();

  const cycleTypeForGoal =
    intendedCycleType ??
    (goal === "embryo-preserve" ? "embryo_freezing" : undefined);

  // Find the most recent closed cycle for this journey. A closed cycle from a
  // different treatment must not become the post-cycle surface just because it
  // happens to be newer.
  const completedCycle = useMemo(() => {
    if (!goal) return null;
    if (!cycles || cycles.length === 0) return null;
    return (
      [...cycles]
        .filter(
          (c) =>
            c.status === "closed" &&
            (cycleTypeForGoal == null || c.cycleType === cycleTypeForGoal),
        )
        .sort((a, b) => {
          const aDate = a.endDate ?? a.createdAt ?? "";
          const bDate = b.endDate ?? b.createdAt ?? "";
          return bDate.localeCompare(aDate);
        })[0] ?? null
    );
  }, [cycles, cycleTypeForGoal, goal]);

  const completedPhasesQuery = useCyclePhases(completedCycle?.id);
  const {
    cycleTotalEggs: totalEggs,
    cycleTotalEmbryos: totalEmbryos,
    cycleTotalSperm: totalSperm,
    cycleStorageGroups: storageGroups,
  } = useFrozenSpecimensForCycle(completedCycle?.id);
  const isLoading =
    cyclesLoading ||
    completedPhasesQuery.isLoading ||
    completedPhasesQuery.isFetching;

  const isComplete = !!completedCycle;

  // Build result from cycle data (graceful — most fields won't exist yet)
  const result: CycleResult | undefined = useMemo(() => {
    if (!completedCycle) return undefined;

    const cycleTypeLabel =
      completedCycle.cycleType === "egg_freezing"
        ? "Egg Freezing"
        : completedCycle.cycleType === "embryo_freezing"
          ? "Embryo Freezing"
          : completedCycle.cycleType === "sperm_freezing"
            ? "Sperm Freezing"
            : completedCycle.cycleType === "ivf"
              ? "IVF"
              : (completedCycle.cycleType?.toUpperCase() ?? "Treatment");

    const metrics = completedPhasesQuery.data?.flatMap(
      (phase) => phase.fertilityMetrics ?? [],
    );
    const metricValue = (type: string) => {
      const metric = metrics?.find((candidate) => candidate.type === type);
      return metric?.value == null ? undefined : metric.value;
    };

    return {
      cycleType: cycleTypeLabel,
      eggsRetrieved: metricValue("retrieved_oocytes"),
      matureEggs: metricValue("m2_count"),
      fertilized: metricValue("fertilized_oocytes"),
      blastocysts: metricValue("embryos_day_5"),
      frozen: metricValue("frozen_embryo"),
      pregnancyResult:
        completedCycle.outcome?.type === "pregnancy"
          ? "positive"
          : completedCycle.outcome?.type === "no_pregnancy"
            ? "negative"
            : null,
      interpretation: undefined,
    };
  }, [completedCycle, completedPhasesQuery.data]);

  // Cryo storage — real data from specimens API, graceful fallback
  const cryoStorage: CryoStorageInfo | undefined = useMemo(() => {
    if (!isComplete || !completedCycle) return undefined;
    if (goal !== "preserve" && goal !== "embryo-preserve") return undefined;

    const isEmbryoCycle = completedCycle.cycleType === "embryo_freezing";
    const isSpermCycle = completedCycle.cycleType === "sperm_freezing";
    const count = isEmbryoCycle
      ? totalEmbryos
      : isSpermCycle
        ? totalSperm
        : totalEggs;
    const expectedStorageType = isEmbryoCycle
      ? "embryo"
      : isSpermCycle
        ? "semen"
        : "oocyte";
    const facilityName = storageGroups.find(
      (group) => group.type === expectedStorageType,
    )?.facility;

    return {
      itemCount: count,
      itemType: isEmbryoCycle ? "embryos" : isSpermCycle ? "sperm" : "eggs",
      facilityName,
      annualCost: undefined,
      nextPaymentDate: undefined,
    };
  }, [
    isComplete,
    goal,
    completedCycle,
    totalEggs,
    totalEmbryos,
    totalSperm,
    storageGroups,
  ]);

  // What's next options based on goal
  const whatsNextOptions: WhatsNextOption[] = useMemo(() => {
    if (!isComplete) return [];

    if (goal === "preserve") {
      return [
        {
          id: "freeze-more",
          title: "Freeze more eggs",
          description:
            "Many women do 2-3 cycles. Each cycle adds to your total bank. Your Learn and Test steps carry over.",
          icon: "repeat" as const,
          action: {
            screen: "CycleDetailScreen",
            label: "Start another cycle",
            params: { presetCycleType: "egg_freezing" },
          },
        },
        {
          id: "use-eggs",
          title: "Use my eggs for IVF",
          description:
            "When you're ready, your eggs are thawed, fertilized, and transferred.",
          icon: "baby" as const,
          action: { screen: "FindCareScreen", label: "Find a clinic" },
        },
        {
          id: "store",
          title: "Just store for now",
          description:
            "Your eggs are safely stored. We'll remind you about storage payments.",
          icon: "pause" as const,
          action: { screen: "CryoVaultScreen", label: "View cryo vault" },
        },
        {
          id: "support",
          title: "Talk to someone",
          description:
            "Processing the experience is normal. Connect with support resources.",
          icon: "heart" as const,
          action: { label: "Talk to someone", signal: "open-ai-chat" },
        },
      ];
    }

    if (goal === "embryo-preserve") {
      return [
        {
          id: "freeze-more",
          title: "Create more embryos",
          description: "Additional cycles can increase your total embryo bank.",
          icon: "repeat" as const,
          action: {
            screen: "CycleDetailScreen",
            label: "Start another cycle",
            params: { presetCycleType: "embryo_freezing" },
          },
        },
        {
          id: "transfer",
          title: "Plan an embryo transfer",
          description:
            "When you're ready, a single embryo is thawed and transferred.",
          icon: "baby" as const,
          action: { screen: "CryoVaultScreen", label: "Review embryos" },
        },
        {
          id: "store",
          title: "Just store for now",
          description: "Your embryos are safely stored.",
          icon: "pause" as const,
          action: { screen: "CryoVaultScreen", label: "View cryo vault" },
        },
      ];
    }

    if (goal === "pregnancy") {
      return [
        {
          id: "another-cycle",
          title: "Try another cycle",
          description:
            "Your clinic will propose a modified protocol based on this cycle's data.",
          icon: "repeat" as const,
          action: { screen: "CycleDetailScreen", label: "Start another cycle" },
        },
        {
          id: "break",
          title: "Take a break",
          description:
            "There's no rush. We'll keep your records and plan ready when you are.",
          icon: "pause" as const,
          action: { screen: "CareOverview", label: "View care plan" },
        },
        {
          id: "second-opinion",
          title: "Get a second opinion",
          description:
            "Some patients consult another RE after a cycle. This is normal.",
          icon: "search" as const,
          action: { screen: "FindCareScreen", label: "Find a clinic" },
        },
        {
          id: "support",
          title: "Talk to someone",
          description: "Processing this is important.",
          icon: "heart" as const,
          action: { label: "Talk to someone", signal: "open-ai-chat" },
        },
      ];
    }

    return [
      {
        id: "support",
        title: "Talk to someone",
        description: "Connect with support resources.",
        icon: "heart" as const,
        action: { label: "Talk to someone", signal: "open-ai-chat" },
      },
    ];
  }, [isComplete, goal]);

  if (DEV_FORCE_COMPLETE_CYCLE_OVERRIDE) {
    return {
      isComplete: true,
      isLoading: false,
      result: MOCK_COMPLETED_CYCLE_RESULT,
      cryoStorage: MOCK_COMPLETED_CRYO_STORAGE,
      whatsNextOptions: MOCK_COMPLETED_WHATS_NEXT,
    };
  }

  return {
    isComplete,
    isLoading,
    result,
    cryoStorage,
    whatsNextOptions,
  };
}
